import { match, P } from 'ts-pattern';

import type { StatsigTier } from 'feature-flags';

type ReturnTypes = {
  clientsAppURI: string;
  environment: string;
  sessionsAppURI: string;
  graphqlURI: string;
  participantsAppURI: string;
  enableIntercom: boolean;
  isDemo?: boolean;
  subscriptionURI: string;
  statsigClientKey: string;
  statsigEnvironment: StatsigTier;
  kindeIsDangerouslyUseLocalStorage: boolean;
};

// Statsig environment tier per migration plan:
// production  → app.askable.com, iframe-*.askable.com (embedded prod), demo.askable.com
//               (demo runs the prod gate set)
// staging     → app-dev.askable.com, staging.app.askable.com
// development → localhost + Vercel preview branches
//
// Resolved from hostname (not VITE_ENVIRONMENT) because the dev deployment doesn't
// reliably plumb the env var through, and hostname is the deployment's source of truth.
// `iframe-*` hosts are production-only — the same elsewhere-in-this-file logic that
// disables Intercom inside iframes lives in the PROD switch case.
function resolveStatsigEnvironment(isDemo: boolean, hostname: string): StatsigTier {
  return match({ isDemo, hostname })
    .with({ hostname: P.union('localhost', '0.0.0.0', P.string.startsWith('127.')) }, () => 'development' as const)
    .with({ isDemo: true }, () => 'production' as const)
    .with({ hostname: P.union('app.askable.com', P.string.startsWith('iframe-')) }, () => 'production' as const)
    .with({ hostname: P.union('app-dev.askable.com', 'staging.app.askable.com') }, () => 'staging' as const)
    .otherwise(() => 'development');
}

const globalVariables = {
  getEnvironmentVariables(): ReturnTypes {
    const envVariablesToReturn = (() => {
      const envVariables = {
        clientsAppURI: window.location.origin,
        environment: import.meta.env.VITE_ENVIRONMENT || 'DEV',
        sessionsAppURI: 'https://sessions-dev.askable.com',
        graphqlURI: 'https://graphql-dev.askable.com/graphql',
        participantsAppURI: 'https://my-dev.askable.com',
        subscriptionURI: 'wss://graphql-dev.askable.com/graphql',
        enableIntercom: !window.location.hostname.startsWith('iframe-'),
        isDemo: window.location.hostname === 'demo.askable.com' || localStorage.getItem('isDemo') === 'true',
        statsigClientKey: import.meta.env.VITE_STATSIG_CLIENT_KEY,
        statsigEnvironment: 'development' as StatsigTier,
        kindeIsDangerouslyUseLocalStorage: false,
      };

      envVariables.statsigEnvironment = resolveStatsigEnvironment(envVariables.isDemo, window.location.hostname);

      switch (import.meta.env.VITE_ENVIRONMENT) {
        case 'PR':
          envVariables.graphqlURI = 'https://graphql-dev.askable.com/graphql';
          envVariables.sessionsAppURI = 'https://sessions-dev.askable.com'; // 'http://localhost:3001';
          envVariables.participantsAppURI = 'https://my-dev.askable.com';
          envVariables.subscriptionURI = 'wss://graphql-dev.askable.com/graphql';
          envVariables.enableIntercom = false;
          envVariables.kindeIsDangerouslyUseLocalStorage = true;
          break;
        case 'PREVIEW':
          envVariables.kindeIsDangerouslyUseLocalStorage = true;
        case 'DEV':
          envVariables.sessionsAppURI = envVariables.isDemo
            ? 'https://sessions-demo.askable.com'
            : 'https://sessions-dev.askable.com';
          envVariables.graphqlURI = 'https://graphql-dev.askable.com/graphql';
          envVariables.participantsAppURI = 'https://my-dev.askable.com';
          envVariables.subscriptionURI = 'wss://graphql-dev.askable.com/graphql';
          envVariables.enableIntercom = true;
          break;
        case 'STAGE':
          envVariables.sessionsAppURI = 'https://sessions-staging.askable.com';
          envVariables.graphqlURI = 'https://graphql-staging.askable.com/graphql';
          envVariables.participantsAppURI = 'https://my-staging.askable.com';
          envVariables.subscriptionURI = 'wss://graphql-staging.askable.com/graphql';
          envVariables.enableIntercom = false;
          break;
        case 'PROD':
          envVariables.sessionsAppURI = 'https://sessions.askable.com';
          envVariables.graphqlURI = 'https://graphql.askable.com/graphql';
          envVariables.participantsAppURI = 'https://my.askable.com';
          envVariables.subscriptionURI = 'wss://graphql.askable.com/graphql';
          envVariables.enableIntercom = !window.location.hostname.match(/iframe/);
          break;

        default:
          if (window.location.hostname.match(/localhost/)) {
            envVariables.sessionsAppURI = 'http://localhost:3001';
            envVariables.graphqlURI = 'http://localhost:4000/graphql';
            envVariables.participantsAppURI = 'https://my-dev.askable.com';
            envVariables.subscriptionURI = 'ws://localhost:4000/graphql';
            envVariables.enableIntercom = false;
            envVariables.kindeIsDangerouslyUseLocalStorage = true;
          }
      }
      return envVariables;
    })();
    return envVariablesToReturn;
  },
  getScreenSize() {
    return window.outerWidth;
  },
  phone: '(07) 3040 4719',
  isStaging() {
    return window.location.hostname === 'staging.app.askable.com';
  },
};

export { globalVariables };
